import { Link } from 'react-router-dom';
import {
  UXP_IX_ID,
  AUTH_METHODS,
  REGISTRATION_FEATURES,
  ORG_SETUP_FIELDS,
  USER_MGMT_CAPABILITIES,
  PLATFORM_ROLES,
  SECURITY_CONTROLS,
  ACTIVATION_FEATURES,
  AMAZON_ONBOARDING_STEPS,
  SYNC_DOMAINS,
  AI_ONBOARDING,
  PERSONALIZATION_OPTIONS,
  NOTIFICATION_CHANNELS,
  ONBOARDING_STEPS,
  LANDING_PAGES,
  ANALYTICS_FUNNELS,
  LIVE_ROUTES,
  buildIdentityDashboard,
} from '../../identityxp/framework';
import {
  OnboardingStepper,
  AuthMethodCard,
  RoleBadgeGrid,
  SecurityFeatureList,
  AmazonConnectStep,
  SyncProgressPanel,
  IdentityFunnelChart,
} from '../../components/identityxp';
import { Card, KpiCard } from '../../components/ui';

export function IdentityPlatformPage() {
  const dashboard = buildIdentityDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{UXP_IX_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Enterprise Identity Platform</h1>
        <p className="mt-1 text-sm text-slate-500">
          Authentication, registration, organization onboarding, Amazon connection, and identity governance — the first experience every customer has with AI-COS.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-brand-200 bg-brand-50 px-3 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-100 dark:border-brand-800 dark:bg-brand-950 dark:text-brand-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Auth Methods" value={dashboard.auth_methods.toString()} />
        <KpiCard label="Onboarding Steps" value={dashboard.onboarding_steps.toString()} />
        <KpiCard label="Platform Roles" value={dashboard.platform_roles.toString()} />
        <KpiCard label="Sync Domains" value={dashboard.sync_domains.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Onboarding Journey</h2>
        <p className="mt-1 text-sm text-slate-500">9-step wizard orchestrated by Alpha journey <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">enterprise_onboarding</code></p>
        <div className="mt-4">
          <OnboardingStepper steps={ONBOARDING_STEPS} currentStep={5} />
        </div>
        <div className="mt-4 grid gap-2 sm:grid-cols-3">
          {ONBOARDING_STEPS.map((s) => (
            <Card key={s.step} padding="sm">
              <p className="font-medium">{s.label}</p>
              <p className="mt-1 text-xs text-slate-500">{s.description}</p>
            </Card>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Authentication Methods</h2>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {AUTH_METHODS.map((m) => (
              <AuthMethodCard key={m.id} label={m.label} status={m.status} />
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Registration Features</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {REGISTRATION_FEATURES.map((f) => (
              <span key={f} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{f}</span>
            ))}
          </div>
          <h3 className="mt-6 text-sm font-semibold">Organization Setup Fields</h3>
          <div className="mt-2 flex flex-wrap gap-2">
            {ORG_SETUP_FIELDS.map((f) => (
              <span key={f} className="rounded-lg border border-brand-200 bg-brand-50 px-2 py-1 text-xs text-brand-700 dark:border-brand-800 dark:bg-brand-950 dark:text-brand-300">{f}</span>
            ))}
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Platform Role Model</h2>
        <div className="mt-4">
          <RoleBadgeGrid roles={PLATFORM_ROLES} />
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Security Architecture</h2>
          <Card className="mt-4" padding="sm">
            <SecurityFeatureList features={SECURITY_CONTROLS} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">User Management</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {USER_MGMT_CAPABILITIES.map((c) => (
              <span key={c} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{c}</span>
            ))}
          </div>
          <h3 className="mt-6 text-sm font-semibold">Subscription & Activation</h3>
          <div className="mt-2 flex flex-wrap gap-2">
            {ACTIVATION_FEATURES.map((f) => (
              <span key={f} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{f}</span>
            ))}
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Amazon Onboarding</h2>
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <AmazonConnectStep marketplace="Amazon.in" />
          <Card padding="sm">
            <h3 className="text-sm font-semibold">Connection Steps</h3>
            <ol className="mt-3 list-decimal space-y-1 pl-4 text-sm text-slate-600 dark:text-slate-400">
              {AMAZON_ONBOARDING_STEPS.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Initial Synchronization</h2>
        <Card className="mt-4" padding="md">
          <SyncProgressPanel domains={SYNC_DOMAINS} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">AI Onboarding</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {AI_ONBOARDING.map((a) => (
              <span key={a} className="rounded-lg border border-ai-200 bg-ai-50 px-2 py-1 text-xs text-ai-700 dark:border-ai-800 dark:bg-ai-950 dark:text-ai-300">{a}</span>
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Personalization & Notifications</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {PERSONALIZATION_OPTIONS.map((p) => (
              <span key={p} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{p}</span>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {NOTIFICATION_CHANNELS.map((n) => (
              <span key={n} className="rounded-lg border border-brand-200 bg-brand-50 px-2 py-1 text-xs text-brand-700 dark:border-brand-800 dark:bg-brand-950 dark:text-brand-300">{n}</span>
            ))}
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Analytics Funnels</h2>
        <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <Card padding="sm">
            <IdentityFunnelChart stages={ANALYTICS_FUNNELS} funnel="registration" title="Registration Funnel" />
          </Card>
          <Card padding="sm">
            <IdentityFunnelChart stages={ANALYTICS_FUNNELS} funnel="amazon_connection" title="Amazon Connection Funnel" />
          </Card>
          <Card padding="sm">
            <IdentityFunnelChart stages={ANALYTICS_FUNNELS} funnel="onboarding_completion" title="Onboarding Completion" />
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Public Landing Experience</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {LANDING_PAGES.map((p) => (
            <span key={p} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{p}</span>
          ))}
        </div>
      </section>

      <p className="text-xs text-slate-500">
        Spec: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">docs/uxp/UXP-00001_ENTERPRISE_IDENTITY_PLATFORM.md</code>
        {' · '}
        Registry: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">pkg/identityxp/</code>
        {' · '}
        Auth service: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">services/auth/</code>
      </p>
    </div>
  );
}
